<template>
  <div :class="{'is-sidebar': isSidebar}">
    <Can :custom="canEdit || canView">
      <template #yes>
        <ld-form :validate-on-mount="isEdit" v-if="!isEdit || (isEdit && patient.id)" without-content-wrapper header-class="patient-add-edit-header">
          <template #header="{ handleSubmit, meta: { valid } }" v-if="!isSidebar">
            <div class="is-flex is-justify-content-space-between">
              <content-header :title="`${isEdit ? 'Edit' : 'Create'} Patient`" >
                <template #button>
                  <div class="right-buttons">
                    <template v-if="isEdit">
                      <button class="button is-rounded is-primary" @click="openSidebar('field-history')">
                      <span class="icon">
                        <i class="mdi mdi-history"></i>
                      </span>
                        <span>Field History</span>
                      </button>
                      <button class="button is-rounded is-primary" @click="openSidebar('merge-history')">
                      <span class="icon">
                        <i class="mdi mdi-merge"></i>
                      </span>
                        <span>Merge History</span>
                      </button>
                      <button class="button is-rounded is-primary" @click="openSidebar('adt-history')">
                      <span class="icon">
                        <i class="mdi mdi-swap-horizontal"></i>
                      </span>
                        <span>ADT History</span>
                      </button>
                    </template>
                  </div>
                </template>
              </content-header>
              <div>
                <div class="action-buttons" v-if="moveToDivisionButtonVisible">
                  <button class="button is-text" @click="resetMrnCheck">No</button>
                  <button class="button is-success" @click="movePatient">Yes</button>
                </div>
                <div v-else class="action-buttons">
                  <button v-if="!updatePatient && !isEdit" @click="$router.push({ name: 'patients.index' })" class="button is-text">
                    Cancel
                  </button>
                  <button class="button is-success" :disabled="!valid || !canEdit" @click="handleSubmit(buttonAction)">{{ actionButtonText }}</button>
                </div>
              </div>
            </div>
          </template>
          <template #default>
            <div v-if="step == PATIENT_CREATION_STEPS.MRN_CHECK" class="box">
              <input-with-validation :disabled="isReadonly" b-name="input" field-label="MRN" :rules="mrnValidationRules" v-model="patient.mrn" @input="resetMrnCheck"></input-with-validation>
              <b-message v-if="mrnError" type="is-warning">
                <p>{{ mrnError }}</p>
                <div v-if="dupPatient" class="dup-patient">
                  <p><span>Name:</span> {{ $helpers.mask('patient_name', dupPatient.name) }}</p>
                  <p><span>DOB:</span> {{ dupPatient.dob ? $helpers.mask('dob', $helpers.formatDate(dupPatient.dob, 'L', true)) : 'N/A' }} </p>
                </div>
              </b-message>
              <b-message v-if="mrnError == false" type="is-success">MRN is valid. Please click continue button below to enter patient details</b-message>
            </div>
            <div v-else-if="step == PATIENT_CREATION_STEPS.PATIENT_DETAILS">
              <div v-if="patient.ai_snapshot && $store.state.currentUser.enabled_flags.includes('crystal_ball')" class="ai-snapshot-container">
                <div class="ai-snapshot-content-wrapper">
                  <img class="ld-ai-logo" src="@/assets/icons/ai-summary-white.svg" width="20" />
                  <b-collapse v-model="aiSnapshotVisibility">
                    <template #trigger>
                      <h3 class="ai-snapshot-title">
                        <i class="mdi mr-1" :class="aiSnapshotVisibility ? 'mdi-chevron-down' : 'mdi-chevron-right'"></i>
                        Live Room Status - {{ patient.room?.name }}
                      </h3>
                    </template>
                    <div class="ai-snapshot-content">
                      <div class="ai-snapshot-text">{{ patient.ai_snapshot }}</div>
                      <div class="ai-snapshot-timestamp">
                        <button class="refresh-button" @click="refreshSnapshot" v-if="canRefreshSnapshot">
                          <i class="mdi mdi-refresh"></i>
                        </button>
                        <span>Last updated: {{ $helpers.formatDate(patient.ai_snapshot_updated_at) }}</span>
                      </div>
                    </div>
                  </b-collapse>
                </div>
              </div>
              <patient-section v-if="shouldShowSection('personal')" title="Personal" has-required-field v-model:is-open="sectionStates.personal">
                <div class="columns">
                  <div v-if="shouldShowField('mrn')" class="column is-3">
                    <input-with-validation :disabled="this.step == PATIENT_CREATION_STEPS.PATIENT_DETAILS" b-name="input" field-label="MRN" :rules="mrnValidationRules" v-model="patient.mrn">
                      <template #custom>
                        <div class="mt-2">
                          <button :disabled="isReadonly" class="button is-small is-rounded" @click="changeMrnButtonAction">
                    <span class="icon">
                      <span class="mdi mdi-refresh"></span>
                    </span>
                            <span>Change MRN</span>
                          </button>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                  <div v-if="shouldShowField('room')" class="column is-3">
                    <input-with-validation field-label="Room">
                      <template #custom>
                        <div class="room-selector-wrapper">
                          <b-autocomplete ref="room" :disabled="isReadonly" v-model="roomName" field="name" :data="filteredRooms" @typing="filterRooms" :open-on-focus="true" @select="onRoomSelect">
                            <template #default="{ option }">
                              <p>{{ option.name }}</p>
                            </template>
                            <template #empty>
                              <p>No room found</p>
                            </template>
                          </b-autocomplete>
                          <router-link
                              v-if="roomVideoDevice && roomVideoDevice.id && roomVideoDevice.kind === 'fixed' && $can('Edit', 'VideoDevice')"
                              :to="{ name: 'hospital.video_devices.edit', params: { id: roomVideoDevice.id } }"
                              target="_blank"
                              class="camera-link"
                              v-tooltip="`${roomVideoDevice.name} (${roomVideoDevice.status})`">
                            <i class="mdi mdi-video-outline camera-icon"></i>
                          </router-link>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                </div>
                <div class="columns">
                  <div v-if="shouldShowField('first_name')" class="column is-3">
                    <input-with-validation :disabled="isReadonly" b-name="input" rules="required" field-label="First Name" v-model="patient.first_name"></input-with-validation>
                  </div>
                  <div v-if="shouldShowField('middle_name')" class="column is-3">
                    <input-with-validation :disabled="isReadonly" b-name="input" field-label="Middle Name" v-model="patient.middle_name"></input-with-validation>
                  </div>
                  <div v-if="shouldShowField('last_name')" class="column is-3">
                    <input-with-validation :disabled="isReadonly" b-name="input" rules="required" field-label="Last Name" v-model="patient.last_name"></input-with-validation>
                  </div>
                  <div v-if="shouldShowField('suffix')" class="column is-3">
                    <input-with-validation :disabled="isReadonly" b-name="input" field-label="Suffix" v-model="patient.suffix"></input-with-validation>
                  </div>
                </div>
                <div class="columns">
                  <div v-if="shouldShowField('dob')" class="column is-3">
                    <input-with-validation :disabled="isReadonly" b-name="datepicker" field-label="DOB" :years-range="[-150,1000]" :max-date="new Date()" v-model="patient.dob_date"></input-with-validation>
                  </div>
                </div>
                <div v-if="shouldShowField('gender')" class="columns">
                  <div class="column is-12">
                    <input-with-validation field-label="Gender" rules="required" v-model="patient.gender">
                      <template #custom>
                        <div class="is-flex">
                          <input-with-validation :disabled="isReadonly" b-name="radio" v-model="patient.gender" validation-label="Gender" rules="required" :native-value="'male'">Male</input-with-validation>
                          <input-with-validation :disabled="isReadonly" b-name="radio" v-model="patient.gender" validation-label="Gender" rules="required" :native-value="'female'">Female</input-with-validation>
                          <input-with-validation :disabled="isReadonly" b-name="radio" v-model="patient.gender" validation-label="Gender" rules="required" :native-value="'other'">Other</input-with-validation>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                </div>
                <div class="columns" v-if="shouldShowField('active')">
                  <div class="column is-12">
                    <input-with-validation field-label="Active" v-model="patient.active">
                      <template #custom>
                        <div class="is-flex">
                          <input-with-validation  b-name="radio" v-model="patient.active" :disabled="isReadonly" validation-label="Active" :native-value="true">Yes</input-with-validation>
                          <input-with-validation b-name="radio" v-model="patient.active" :disabled="isReadonly" validation-label="Active" :native-value="false">No</input-with-validation>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                </div>
                <div v-if="shouldShowField('virtual_status')" class="columns">
                  <div class="column is-12">
                    <input-with-validation field-label="Virtual Status" v-model="patient.virtual_status_id">
                      <template #custom>
                        <div>
                          <b-select placeholder="Select Status" expanded v-model="patient.virtual_status_id">
                            <option value="">N/A</option>
                            <option v-for="status in virtualStatusValues" :key="status.id" :value="status.id">
                              {{ status.label }}
                            </option>
                          </b-select>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                </div>
              </patient-section>
              <patient-section v-if="shouldShowSection('digital_signs')" title="Digital Signs & Family Connect" v-model:is-open="sectionStates.digital_signs">
                <div class="columns">
                  <div class="column is-6" v-if="shouldShowField('pain_scale') || shouldShowField('precautions')">
                    <div v-if="shouldShowField('pain_scale')" class="columns">
                      <div class="column is-12">
                        <input-with-validation field-label="Pain Scale" v-model="patient.pain_scale.level" b-name="select" expanded>
                          <option :value="null">N/A</option>
                          <option v-for="i in 10" :key="i">{{ i }}</option>
                        </input-with-validation>
                      </div>
                    </div>
                    <template v-if="shouldShowField('precautions')">
                      <div class="columns" v-for="(_, i) in patient.precautions" :key="i">
                        <div class="column is-12">
                          <div class="columns">
                            <div class="column">
                              <input-with-validation b-name="select" field-label="Precaution" expanded v-model="patient.precautions[i].name" @change="setPrecautionLevels(patient.precautions[i].name, i)">
                                <option value="">N/A</option>
                                <option v-for="(precaution, index) in Object.entries(PRECAUTIONS)" :key="index" :value="precaution[0]">{{ precaution[1] }}</option>
                              </input-with-validation>
                            </div>
                            <div class="column">
                              <input-with-validation b-name="select" field-label="Level" expanded v-model="patient.precautions[i].level">
                                <option value="">N/A</option>
                                <template v-if="precautionLevels[i]">
                                  <option v-for="(precautionLevel, index) in Object.entries(precautionLevels[i])" :key="index" :value="precautionLevel[0]">{{ precautionLevel[1] }}</option>
                                </template>
                              </input-with-validation>
                            </div>
                          </div>
                        </div>
                      </div>
                    </template>
                  </div>
                  <div class="column is-6" v-if="shouldShowField('family_note') || shouldShowField('room_note')">
                    <div class="columns is-flex-wrap-wrap">
                      <div v-if="shouldShowField('family_note')" class="column is-12">
                        <input-with-validation field-label="Family note" b-name="input" :rules="{ max: 500 }" type="textarea" v-model="patient.family_note" expanded maxlength="500"></input-with-validation>
                      </div>
                      <div v-if="shouldShowField('room_note')" class="column is-12">
                        <input-with-validation field-label="Room note" b-name="input" :rules="{ max: 150 }" type="textarea" v-model="patient.room_note" expanded maxlength="150"></input-with-validation>
                      </div>
                    </div>
                  </div>
                </div>
                <div class="columns">

                </div>
              </patient-section>
              <patient-section v-if="shouldShowSection('general')" title="General" v-model:is-open="sectionStates.general">
                <div class="columns">
                  <div v-if="shouldShowField('pronoun')" class="column is-6">
                    <input-with-validation field-label="Pronoun"  v-model="patient.pronoun">
                      <template #custom>
                        <div class="is-flex">
                          <input-with-validation b-name="radio" v-model="patient.pronoun" validation-label="Gender" :native-value="'he, him'">He, Him</input-with-validation>
                          <input-with-validation b-name="radio" v-model="patient.pronoun" validation-label="Gender" :native-value="'she, her'">She, Her</input-with-validation>
                          <input-with-validation b-name="radio" v-model="patient.pronoun" validation-label="Gender" :native-value="'they, them'">They, Them</input-with-validation>
                        </div>
                      </template>
                    </input-with-validation>
                  </div>
                  <div v-if="shouldShowField('language')" class="column is-6">
                    <input-with-validation field-label="Language" b-name="select" v-model="patient.language_id" expanded placeholder="Select a language">
                      <option :key="index" :value="lang.id" v-for="(lang, index) in languages">
                        {{ lang.name }}
                      </option>
                    </input-with-validation>
                  </div>
                </div>
                <div class="columns">
                  <div v-if="shouldShowField('note')" class="column is-6">
                    <input-with-validation field-label="Note" b-name="input" :rules="{ max: 150 }" v-model="patient.note" expanded maxlength="150"></input-with-validation>
                  </div>
                  <div v-if="shouldShowField('attributes')" class="column is-6">
                    <input-with-validation  field-label="Attributes" expanded>
                      <template #custom>
                        <ld-taginput v-model="patient.patient_tags" :data="tags" :input-props="{ field: 'name' }">
                          <template #not-found>
                            <p>No attribute found</p>
                          </template>
                          <template #all-selected>
                            <p>All attributes are selected</p>
                          </template>
                        </ld-taginput>
                      </template>
                    </input-with-validation>
                  </div>
                </div>
                <div class="columns">
                  <div v-if="shouldShowField('aimee_memory_personal')" class="column is-6">
                    <input-with-validation field-label="Aimee Personal Memory" b-name="input" type="t extarea" v-model="patient.aimee_memory_personal" expanded rows="3"></input-with-validation>
                  </div>
                  <div v-if="shouldShowField('aimee_memory_clinical')" class="column is-6">
                    <input-with-validation field-label="Aimee Clinical Memory" b-name="input" type="textarea" v-model="patient.aimee_memory_clinical" expanded rows="3"></input-with-validation>
                  </div>
                </div>
              </patient-section>

              <div class="custom-fields">
                <custom-fields-renderer model="Patient" v-model="patient.custom_fields" :field-visibility="fieldVisibility" :is-sidebar="isSidebar" v-model:section-states="sectionStates"></custom-fields-renderer>
              </div>
            </div>
          </template>
          <template #sidebar>
            <field-changes-sidebar v-if="fieldHistoryOpen" object-type="patient" :object-id="$route.params.id" @close="closeSidebar"></field-changes-sidebar>
            <patient-merge-history-sidebar v-if="mergeHistoryOpen" :patient-id="$route.params.id" @close="closeSidebar"></patient-merge-history-sidebar>
            <adt-history-sidebar v-if="adtHistoryOpen" :patient-id="$route.params.id" @close="closeSidebar"></adt-history-sidebar>
          </template>
        </ld-form>
      </template>
      <template #no>
        <no-permission-message></no-permission-message>
      </template>
    </Can>
  </div>
</template>

<script>
import to from '@/lib/to'
import InputWithValidation from "@/components/validation/InputWithValidation";
import LdForm from "@/components/LdForm";
import ContentHeader from "@/components/ContentHeader";
import NoPermissionMessage from "@/components/permissions/NoPermissionMessage";
import LdTaginput from "@/components/LdTaginput";
import DivisionPickerStateMixin from "@/components/mixins/DivisionPickerStateMixin";
import FieldChangesSidebar from "@/components/FieldChangesSidebar.vue";
import PatientMergeHistorySidebar from "@/components/PatientMergeHistorySidebar.vue";
import AdtHistorySidebar from "@/components/AdtHistorySidebar.vue";
import CustomFieldsRenderer from "@/components/custom_fields/CustomFieldsRenderer";

import {toRaw} from "vue";
import {PRECAUTIONS, PRECAUTIONS_LEVEL} from "@/lib/config";
import PatientSection from "@/components/patient_overview/PatientSection.vue";
import { getFieldVisibility } from "@/config/field-configs";
import {applyPermissions} from "@/lib/Permissions";
import {SERVER_EVENT, CHANNEL} from "@/lib/Pusher";
import {isVideoDeviceOffline} from "@/lib/PatientUtils";



const PATIENT_CREATION_STEPS = Object.freeze({
  MRN_CHECK: 'mrn-check',
  PATIENT_DETAILS: 'patient-details'
});

export default {
  name: "AddPatient",
  components: {
    PatientSection,
    CustomFieldsRenderer,
    PatientMergeHistorySidebar,
    AdtHistorySidebar,
    FieldChangesSidebar,
    LdTaginput, NoPermissionMessage, ContentHeader, LdForm, InputWithValidation},
  mixins: [DivisionPickerStateMixin],
  divisionPickerState: 'readonly',
  inject: ['updatePatient'],
  props: {
    isSidebar: {
      type: Boolean,
      default: false
    },
    patientId: {
      type: Number
    }
  },

  beforeUnmount() {
    this.hospitalChannel?.unbind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)
  },

  async beforeMount() {
    this.aiSnapshotVisibility = !this.isSidebar
    const requests = [
      this.$http.get('/languages'),
      this.$http.get('/tags/dropdown'),
      this.$http.get('/dynamic_fields', { params: { table_name: 'patients', field_name: 'status' } })
    ]
    if (this.isEdit) {
      this.step = PATIENT_CREATION_STEPS.PATIENT_DETAILS
      requests.push(this.$http.get(`/patients/${this.getPatientId()}`))
    } else {
      this.setDefaultPrecautions()
      this.step = PATIENT_CREATION_STEPS.MRN_CHECK
    }

    let patient = null
    if (this.canEdit || this.canView) {
      let [langTagsErr, langTagsResponse] = await to(Promise.all(requests))
      if (!langTagsErr) {
        const [{data: languages}, {data: tags}, {data: dynamicFieldsResponse}, {data: patientData} = {}] = langTagsResponse
        patient = patientData
        this.languages = languages
        this.tags = tags

        if (dynamicFieldsResponse && dynamicFieldsResponse.length > 0) {
          this.virtualStatusValues = dynamicFieldsResponse[0].values
            .filter(val => val.is_enabled)
            .sort((a, b) => a.sort_order - b.sort_order)
        }

        this.fieldVisibility = getFieldVisibility('patients', this.$store)
        await this.fetchPagePreference()
        await this.loadSectionStates()
      }
      if (patient) {
        this.patient = patient
        this.patient.virtual_status = this.virtualStatusValues.find(x => x.id == this.patient.virtual_status_id)
        this.patient.precautions.forEach((_, i) => {
          this.setPrecautionLevels(this.patient.precautions[i].name, i)
        })
        this.setDefaultPrecautions()
        // this.originalPatientStatus = patient.status
        this.roomName = patient.room?.name
        this.isReadonly = patient.patient_monitors.some(x => x.status == 'active' || x.status == 'paused') || (this.canView && !this.canEdit)
        this.patient.dob_date = patient.dob ? this.$helpers.getDate({ date: patient.dob }).utc().toDate() : null;
        this.patient.patient_tags = this.patient.patient_tags.map(x => x.tag )
      }
    }

    if (this.$can('Edit', 'Patient')) {
      let roomParams = {}
      if (patient) {
        roomParams['room_id'] = patient.room_id
        roomParams['division_id'] = patient.division_id
      }
      const [err, response] = await to(this.$http.get('/rooms/available', { params: roomParams }))
      if (!err) {
        this.rooms = this.filteredRooms = response.data
      }

      if (this.isEdit) {
        this.hospitalChannel = this.$pusher.subscribe(CHANNEL.FOR_HOSPITAL({
          env: import.meta.env.VITE_ENVIRONMENT_NAME,
          hospital: this.$store.state.currentHospital
        }))

        this.hospitalChannel.bind(SERVER_EVENT.AI_SNAPSHOT_UPDATED, this.handleAiSnapshotUpdated)

        if (patient.room) {
          this.$nextTick(() => {
            this.$refs.room?.setSelected(patient.room)
          })
          if (patient.room.id) {
            await this.fetchRoomVideoDevice(patient.room.id)
          }
        }
      }
    }
  },
  watch: {
    sectionStates: {
      handler() {
        this.saveSectionStates()
      },
      deep: true
    }
  },
  computed: {
    mrnChars() {
      return this.$store.state.currentHospital.mrn_chars
    },
    mrnValidationRules() {
      const rules = { required: true }
      if (this.mrnChars) {
        rules['length'] = +this.mrnChars
      }
      return rules
    },
    actionButtonText() {
      if (this.step == PATIENT_CREATION_STEPS.MRN_CHECK) {
        if (this.mrnError == false) {
          return 'Continue'
        }
        return 'Check MRN'
      } else {
        return this.isEdit ? 'Update' : 'Create'
      }
    },
    isEdit() {
      return Object.prototype.hasOwnProperty.call(this.$route.params, 'id') || this.isSidebar
    },
    canRefreshSnapshot() {
      return !!this.activeMonitorId && !isVideoDeviceOffline(this.patient)
    },
    activeMonitorId() {
      return this.patient?.patient_monitors?.find(m => m.status === 'active')?.id
    }
  },
  methods: {
    applyPermissions,
    getPatientId() {
      return this.isSidebar ? this.patientId : this.$route.params.id
    },
    setPrecautionLevels(precaution, index) {
      this.precautionLevels[index] = PRECAUTIONS_LEVEL[precaution]
    },
    setDefaultPrecautions() {
      const placeholder = { name: '', level: '' }
      /* this.patient.precautions array should always have four objects. if for some reason it already has one, fill the second one with placeholder,
       if it has none fill both with placeholder */
      let len = 4 - this.patient.precautions.length;
      for (let i = 0; i < len; i++) {
        this.patient.precautions.push({...placeholder})
      }
    },
    filterRooms(val) {
      const searchText = val.trim(),
          regex = new RegExp(this.$helpers.sanitizeInputForRegExp(searchText), 'i');
      if (searchText) {
        this.filteredRooms = this.rooms.filter(x => regex.test(x.name))
      } else {
        this.filteredRooms = this.rooms
      }
    },
    async onRoomSelect(room) {
      this.patient.room_id = room ? room.id : null
      this.roomVideoDevice = null

      if (room && room.id) {
        await this.fetchRoomVideoDevice(room.id)
      }
    },
    async fetchRoomVideoDevice(roomId) {
      if (this.$can('View', 'VideoDevice')) {
        const [err, response] = await to(this.$http.get(`/video_devices/room/${roomId}?no_pager=true`))
        if (!err && response.data && response.data.length > 0) {
          const fixedCamera = response.data.find(device => device.kind === 'fixed')
          if (fixedCamera) {
            this.roomVideoDevice = fixedCamera
          }
        }
      }
    },
    async savePatient({ redirect = true} = {}) {
      let patient_tags = [];

      const _save = async () => {
        if (this.patient.patient_tags) {
          patient_tags = this.patient.patient_tags.map(x => x.id);
        }
        const dob = this.patient.dob_date ? this.$helpers.getDate({ date: this.patient.dob_date, withoutTimezone: true }).format('YYYY-M-D') : null;

        let endpoint = '/patients'
        let messageText = 'Patient created successfully'
        let httpMethod = 'post'
        if (this.isEdit) {
          endpoint += `/${this.getPatientId()}`
          messageText = 'Patient updated successfully'
          httpMethod = 'put'
        }
        this.patient.pain_scale.date = this.$helpers.getDate().toISOString()
        const [error, ] = await to(this.$http[httpMethod](endpoint, {
          ...this.patient,
          patient_tags, dob,
        }, { handleErrors: true }))
        if (error) {
          if (error.response.data.type == 'Patient::MrnNotUnique') {
            const dupPatient = JSON.parse(error.response.data.error)
            const patientLink = this.$router.resolve({
              name: 'patients.edit',
              params: { id: dupPatient.patient_id },
              query: { division_id: dupPatient.division_id }
            }).fullPath;
            this.$buefy.dialog.alert({
              title: 'Error',
              message: `<a target="_blank" href="${patientLink}">Patient</a> with given MRN already exists.`
            });
          } else {
            this.$store.commit('setNetworkErrors', [error])
          }
        }
        if (!error && redirect) {
          let to = { name: 'patients.index' }
          if (this.updatePatient) {
            to = { name: 'patients.overview.edit', params: { id: this.patient.id } }
            const rawPatient = toRaw(this.patient)
            rawPatient.patient_tags = rawPatient.patient_tags.map(x => toRaw(x))
            rawPatient.virtual_status = toRaw(this.virtualStatusValues.find(x => x.id == this.patient.virtual_status_id))

            this.updatePatient(structuredClone(rawPatient))
          }
          this.$helpers.redirectWithMessage({
            to, messageText
          })
        }

        return [error]
      };

      /*
      if (this.originalPatientStatus == 'active' && this.patient.status == 'inactive') {
        this.$buefy.dialog.confirm({
          message: 'Please confirm you would like to make this patient Inactive in the system.',
          cancelText: 'Cancel',
          confirmText: 'Confirm',
          type: 'is-danger',
          focusOn: 'cancel',
          closeOnConfirm: true,
          confirmCallback: async () => {
            _save()
          }
        })
      } else {
        _save()
      }
      */

      return _save()
    },
    async checkMrn() {
      this.dupPatient = null
      const [err] = await to(this.$http.get(`/patients/is_mrn_editable/${this.patient.mrn}/${this.patient.id || ''}`, { handleErrors: true }))
      if (err) {
        if (['Patient::MonitoringInProgress', 'Patient::MrnNotUnique'].includes(err.response.data.type)) {
          const errorJson = JSON.parse(err.response.data.error)
          this.dupPatient = { name: errorJson.patient_name, dob: errorJson.dob }
          if (err.response.data.type == 'Patient::MonitoringInProgress') {
            this.mrnError = "Patient with this MRN is currently in an active monitoring session. Please stop the session or ask an administrator with access to do so"
          } else if (err.response.data.type == 'Patient::MrnNotUnique') {
            const currentDivisionName = this.$store.state.currentUser.divisions.find(x => x.id == this.$store.state.divisionId).name;
            if (errorJson.division_id == this.$store.state.divisionId) {
              this.mrnError = `Patient with given MRN already exists in ${currentDivisionName} hospital`
            } else {
              if (this.isEdit) {
                this.mrnError = `Patient with given MRN already exists in ${errorJson.division_name} hospital`
              } else {
                this.mrnError = `This patient is currently in ${errorJson.division_name}. Would you like to move them to ${currentDivisionName}?`
                this.patientIdToMove = errorJson.patient_id
                this.moveToDivisionButtonVisible = true
              }
            }
          }
        }
      } else {
        this.mrnError = false
      }
    },
    buttonAction() {
      if (this.step == PATIENT_CREATION_STEPS.MRN_CHECK) {
        if (this.mrnError == false) {
          this.step = PATIENT_CREATION_STEPS.PATIENT_DETAILS
        } else {
          this.checkMrn()
        }
      } else {
        this.savePatient()
      }
    },
    changeMrnButtonAction() {
      this.step = PATIENT_CREATION_STEPS.MRN_CHECK
      this.resetMrnCheck()
    },
    resetMrnCheck() {
      this.mrnError = null
      this.moveToDivisionButtonVisible = false
    },
    async movePatient() {
      const [err] = await to(this.$http.post(`/patients/${this.patientIdToMove}/move_to_division`, { division_id: this.$store.state.divisionId }))
      if (!err) {
        this.$router.push({ name: 'patients.edit', params: { id: this.patientIdToMove }})
      }
    },
    openSidebar(content) {
      this.fieldHistoryOpen = content == 'field-history'
      this.mergeHistoryOpen = content == 'merge-history'
      this.adtHistoryOpen = content == 'adt-history'
    },
    closeSidebar() {
      this.fieldHistoryOpen = false
      this.mergeHistoryOpen = false
      this.adtHistoryOpen = false
    },

    shouldShowField(fieldKey) {
      return this.fieldVisibility.fields[fieldKey]?.visible ?? true
    },

    shouldShowSection(sectionKey) {
      return Object.values(this.fieldVisibility.fields)
        .filter(field => field.section === sectionKey)
        .some(field => field.visible)
    },


    async fetchPagePreference() {
      const endpoint = this.isSidebar ? '/page_preferences/for_current_user/video_visit' : '/page_preferences/for_current_user/patients';
      const [err, response] = await to(this.$http.get(endpoint))
      if (!err && response.data) {
        const pagePreference = response.data

        if (pagePreference.settings && pagePreference.settings.field_visibility) {
          Object.entries(pagePreference.settings.field_visibility).forEach(([fieldKey, visible]) => {
            if (this.fieldVisibility.fields[fieldKey]) {
              this.fieldVisibility.fields[fieldKey].visible = visible
            }
          })
        }
      }
    },
    async refreshSnapshot() {
      if (!this.activeMonitorId) {
        return
      }
      const [err] = await to(this.$http.post(`/patient_monitors/${this.activeMonitorId}/refresh_snapshot`))

      this.$buefy.toast.open({
        message: err ? 'Failed to refresh snapshot' : 'Refreshing snapshot...',
        type: err ? 'is-danger' : 'is-success',
        duration: 3000
      })
    },

    handleAiSnapshotUpdated(data) {
      if (data.patient_id === this.patient.id) {
        this.patient.ai_snapshot = data.ai_snapshot
        this.patient.ai_snapshot_updated_at = data.ai_snapshot_updated_at
      }
    },

    async loadSectionStates() {
      const savedStates = await this.$store.dispatch('UserPreferencesStore/get', { key: 'patient_form_sections' })
      if (savedStates && Object.keys(savedStates).length > 0) {
        this.sectionStates = { ...this.sectionStates, ...savedStates }
      }
      if (this.isSidebar) {
        this.sectionStates.digital_signs = true
        this.sectionStates.general = true
      }
    },

    async saveSectionStates() {
      await this.$store.dispatch('UserPreferencesStore/set', {
        key: 'patient_form_sections',
        value: this.sectionStates
      })
    }
  },
  data() {
    return {
      canEdit: this.$can('Edit', 'Patient'),
      canView: this.$can('View', 'Patient'),
      PATIENT_CREATION_STEPS,
      availableCustomFields: [],
      roomName: null,
      isReadonly: false,
      step: PATIENT_CREATION_STEPS.PATIENT_DETAILS,
      rooms: [],
      genders: ['male', 'female', 'other'],
      pronouns: ['he, him', 'she, her', 'they, them'],
      languages: [],
      patient: { custom_fields: {}, precautions: [], pain_scale: { level: null}, virtual_status_id: null, active: true},
      // originalPatientStatus: null,
      tags: [],
      filteredRooms: [],
      fieldHistoryOpen: false,
      mergeHistoryOpen: false,
      adtHistoryOpen: false,
      mrnError: null,
      moveToDivisionButtonVisible: false,
      patientIdToMove: null,
      dupPatient: null,
      PRECAUTIONS,
      precautionLevels: {},
      virtualStatusValues: [],
      roomVideoDevice: null,
      fieldVisibility: {
        sections: {},
        fields: {}
      },
      aiSnapshotVisibility: false,
      hospitalChannel: null,
      sectionStates: {
        personal: true,
        digital_signs: false,
        general: false
      }
    }
  }
}
</script>

<style scoped lang="scss">
  .is-sidebar {
    margin: 0 !important;
    :deep(.card-header-title) {
      font-weight: 500;
      font-size: 11px;
      letter-spacing: 0.3px;
      text-transform: uppercase;
    }
    :deep(.collapse.card) {
      border-radius: 0;
      margin-bottom: 0;
    }
    :deep(.card-header) {
      border-radius: 0;
    }
    :deep(.column) {
      flex: none;
      width: 100%;
      padding-left: 0;
      padding-right: 0;
      .room-selector-wrapper .autocomplete {
        width: 100%;
      }
    }
    :deep(.columns) {
      width: 100%;
      flex-direction: column;
      margin-left: 0;
      margin-right: 0;
    }
    :deep(.card-content) {
      padding: 1rem;
    }
  }
  :deep(.help.counter) {
    float: left;
  }
  .right-buttons {
    margin-left: auto;
    display: flex;
    gap: 10px;
    align-items: center;
  }
  .dup-patient {
    margin-top: 10px;
    p span:first-child {
      font-weight: 500;
    }
  }
  .custom-fields {
    :deep(> div > div) {
      margin-bottom: 20px;
    }
  }
  h1 {
    font-size: 22px;
    font-weight: 400;
    letter-spacing: 0.5px;
    margin-bottom: 30px;
    border-bottom: 1px solid #f2f4fb;
    padding-bottom: 6px;
  }
  :deep(.field-wrapper) {
    margin-bottom: 0;
  }
  .collapse.card {
    margin-bottom: 20px;
  }
  .room-selector-wrapper {
    display: flex;
    align-items: center;
    gap: 10px;

    .camera-link .camera-icon {
      cursor: pointer;
      color: $primary;
      font-size: 16px;
    }
  }
  :deep(.patient-add-edit-header) {
    position: sticky;
    top: 0px;
    z-index: 997;
    background-color: $background-level-0;

    margin: 0 -43px;
    padding: 15px 43px 15px;
    .content__header {
      margin: 0;
      padding: 0;
    }
    .action-buttons {
      margin-top: 0;
    }
  }
  .ai-snapshot-container {
    border-radius: 8px;
    padding: 4px;
    background: linear-gradient(45deg, #A438A8, #7E36B1);
    margin-bottom: 20px;

    .ai-snapshot-content-wrapper {
      position: relative;
    }

    .ld-ai-logo {
      position: absolute;
      right: 5px;
      top: 6px;
    }

    .refresh-button {
      border: none;
      background: none;
      color: #fff;
      cursor: pointer;
      i {
        font-size: 18px;
      }
    }

    i {
      padding-top: 1px;
    }

    .ai-snapshot-title {
      font-size: 15px;
      font-weight: 500;
      color: #fff;
      display: flex;
      align-items: center;
      padding: 8px 10px 7px 10px;
      margin-bottom: 0;
    }

    .ai-snapshot-content {
      position: relative;
      padding: 5px 10px;
    }

    .ai-snapshot-text {
      font-size: 13px;
      line-height: 1.4;
      color: #fff;
      opacity: 0.9;
      margin-bottom: 15px;
      padding-left: 20px;
    }

    .ai-snapshot-timestamp {
      text-transform: uppercase;
      letter-spacing: 0.5px;
      font-size: 10px;
      opacity: 0.7;
      color: #fff;
      font-weight: 500;
      display: flex;
      align-items: center;
      justify-content: flex-end;
    }
  }
</style>
